import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Search } from 'lucide-react'
import { parseAsInteger, parseAsString, parseAsStringLiteral, useQueryState } from 'nuqs'
import { useUrlSearchText } from '@/hooks/use-url-search-text'
import { usePurchaseOrders } from '@/api/hooks'
import { useMyProjectsFilter } from '@/hooks/use-my-projects-filter'
import { useAuth } from '@/hooks/use-auth'
import { PurchaseOrdersTable, PurchaseOrdersTableSkeleton } from '@/components/purchase-orders-table'
import { BulkPOStatusPicker } from '@/components/bulk-po-status-picker'
import { stageLabel } from '@/features/home/model'
import { StageFilterChip } from '@/components/stage-filter-chip'
import { ProjectPriorityFilter } from '@/components/project-priority-filter'
import { ServiceTierFilter } from '@/components/service-tier'
import { PROJECT_PRIORITY_FILTER_OPTIONS, projectPriorityParam } from '@/lib/project-priority'
import { SERVICE_TIER_FILTER_OPTIONS, serviceTierParam } from '@/lib/service-tier'
import { SearchInput } from '@/components/search-input'
import { ListFilter, ListFilters } from '@/components/list-filters'
import { resultCount } from '@/lib/utils'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:80'

export function PurchaseOrdersPage() {
  const [search, setSearch] = useUrlSearchText()
  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1))
  const [stage, setStage] = useQueryState('stage', parseAsString)
  const [priority, setPriority] = useQueryState(
    'priority',
    parseAsStringLiteral(PROJECT_PRIORITY_FILTER_OPTIONS).withDefault('all'),
  )
  const [tier, setTier] = useQueryState('tier', parseAsStringLiteral(SERVICE_TIER_FILTER_OPTIONS).withDefault('all'))
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set())
  const { getAccessToken } = useAuth()

  const { opsPersonId } = useMyProjectsFilter()
  const { data, isLoading } = usePurchaseOrders({
    search: search || undefined,
    stage: stage ?? undefined,
    ops_person_id: opsPersonId,
    project_priority: projectPriorityParam(priority),
    service_tier: serviceTierParam(tier),
    page,
    limit: 20,
  })
  const selectedPOs = (data?.items ?? []).filter((po) => selectedIds.has(po.id))
  const changePage = (next: number) => {
    void setPage(next)
    setSelectedIds(new Set())
  }

  const handleDownloadPdf = async (uuid: string, filename?: string) => {
    const token = await getAccessToken()
    if (!token) return
    const response = await fetch(`${API_BASE_URL}/dashboard/purchase-orders/${uuid}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!response.ok) return
    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename || `PO-${uuid}.pdf`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      <h1 className="text-2xl font-bold tracking-tight">Purchase Orders</h1>

      <ListFilters

        count={resultCount(data?.total, 'purchase order')}
        active={(priority === 'all' ? 0 : 1) + (tier === 'all' ? 0 : 1)}
        onClear={() => {
          void setPriority(null)
          void setTier(null)
          changePage(1)
        }}
        search={
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <SearchInput
              placeholder="Search PO, order, customer, manufacturer, product or tracking..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                changePage(1)
              }}
              className="pl-9"
            />
          </div>
        }
        actions={
          stage ? (
            <StageFilterChip
              label={stageLabel(stage)}
              onClear={() => {
                void setStage(null)
                changePage(1)
              }}
            />
          ) : null
        }
      >
        <ListFilter label="Project priority">
          <ProjectPriorityFilter
            value={priority}
            onValueChange={(value) => {
              void setPriority(value)
              changePage(1)
            }}
          />
        </ListFilter>
        <ListFilter label="Service tier">
          <ServiceTierFilter
            value={tier}
            onValueChange={(value) => {
              void setTier(value)
              changePage(1)
            }}
          />
        </ListFilter>
      </ListFilters>

      {selectedPOs.length > 0 && (
        <div
          role="group"
          aria-label="Selected purchase orders"
          className="sticky top-2 z-20 flex flex-wrap items-center gap-2 rounded-lg border bg-background px-3 py-2 shadow-sm"
        >
          <span className="text-sm font-medium tabular-nums">
            {selectedPOs.length} {selectedPOs.length === 1 ? 'PO' : 'POs'} selected
          </span>
          <BulkPOStatusPicker pos={selectedPOs} onSaved={() => setSelectedIds(new Set())} />
          <Button variant="ghost" size="sm" className="h-8" onClick={() => setSelectedIds(new Set())}>
            Clear
          </Button>
        </div>
      )}

      {isLoading ? (
        <PurchaseOrdersTableSkeleton />
      ) : data?.items?.length ? (
        <PurchaseOrdersTable
          items={data.items}
          onDownloadPdf={handleDownloadPdf}
          selectedIds={selectedIds}
          onSelectedIdsChange={setSelectedIds}
        />
      ) : (
        <div className="py-12 text-center text-muted-foreground">No purchase orders found</div>
      )}

      <div className="flex items-center justify-between px-4 py-3">
        <p className="text-sm text-muted-foreground">
          Page {data?.page ?? 1} of {data?.pages ?? 1} ({data?.total ?? 0} total)
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => changePage(Math.max(1, page - 1))}
            disabled={page <= 1}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => changePage(page + 1)}
            disabled={page >= (data?.pages ?? 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  )
}
